import { GROUP_NAMES } from "components/ReportFilters/constants";
import { COLUMN_TYPE } from "constants/fieldTypes";
import { partition, sortBy } from "lodash";
import isNullishOrBlank from "utils/isNullishOrBlank";
import { isGrouped } from "utils/shreddingTemplate/columnBehaviour";

export const COMMENT_CARD_KEY = "comment";
export const COMMENT_CARD_LABEL = GROUP_NAMES.BY_COMMENT;
export const NOT_SET_LABEL = "Not Set";
export const NOT_ANSWERED_LABEL = "Not Answered";

const FILTERABLE_TYPES = new Set([
  COLUMN_TYPE.singleSelect,
  COLUMN_TYPE.multiSelect,
  COLUMN_TYPE.person,
  COLUMN_TYPE.yesNo,
]);

const COLUMN_CARD_PREFIX = "col:";

export const toColumnCardKey = (columnId) => `${COLUMN_CARD_PREFIX}${columnId}`;

export const toColumnId = (cardKey) =>
  Number(String(cardKey).slice(COLUMN_CARD_PREFIX.length));

export const isColumnCardKey = (cardKey) =>
  String(cardKey).startsWith(COLUMN_CARD_PREFIX);

export const isFilterable = (column) => FILTERABLE_TYPES.has(column?.type);

export const isDefaultCard = (column) =>
  Boolean(column.pinned) && !isGrouped(column);

export const orderFilterableColumns = (columns = []) =>
  partition(
    sortBy(columns.filter(isFilterable), "position"),
    isDefaultCard
  ).flat();

const toMatchKey = (column, value) =>
  column.type === COLUMN_TYPE.person ? String(value).toLowerCase() : value;

const heldValues = (value) => (Array.isArray(value) ? value : [value]);

export const toCellMatcher = (column, { values = [], notSet = false }) => {
  const wanted = new Set(
    values.map((wantedValue) => toMatchKey(column, wantedValue))
  );
  return (value) =>
    isNullishOrBlank(value)
      ? notSet
      : heldValues(value).some((held) => wanted.has(toMatchKey(column, held)));
};

export const rowMatchesColumnFilters = (row, activeColumnFilters) =>
  activeColumnFilters.every(({ column, matchesCell }) =>
    matchesCell(row.values?.[column.id])
  );

const countHeldValues = (column, rows) => {
  const counts = new Map();
  const firstSeen = new Map();
  let notSetCount = 0;

  rows.forEach((row) => {
    const value = row.values?.[column.id];
    if (isNullishOrBlank(value)) {
      notSetCount += 1;
      return;
    }
    const heldByKey = new Map(
      heldValues(value).map((held) => [toMatchKey(column, held), held])
    );
    heldByKey.forEach((held, key) => {
      counts.set(key, (counts.get(key) ?? 0) + 1);
      if (!firstSeen.has(key)) {
        firstSeen.set(key, held);
      }
    });
  });

  return { counts, firstSeen, notSetCount };
};

const selectOptions = (column) =>
  sortBy(column.values, "position").map(({ id, value }) => ({
    value: id,
    label: value,
  }));

const YES_NO_OPTIONS = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const personOptions = (firstSeen, people) =>
  [...firstSeen.values()]
    .map((userName) => ({
      value: userName,
      label: people?.[userName]?.displayName || userName,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

const valueOptions = (column, firstSeen, people) => {
  if (column.type === COLUMN_TYPE.yesNo) {
    return YES_NO_OPTIONS;
  }
  if (column.type === COLUMN_TYPE.person) {
    return personOptions(firstSeen, people);
  }
  return selectOptions(column);
};

export const notSetLabel = (column) =>
  column.type === COLUMN_TYPE.yesNo ? NOT_ANSWERED_LABEL : NOT_SET_LABEL;

export const buildFilterOptions = (column, rows = [], people = {}) => {
  const { counts, firstSeen, notSetCount } = countHeldValues(column, rows);
  const options = valueOptions(column, firstSeen, people).map((option) => ({
    ...option,
    count: counts.get(toMatchKey(column, option.value)) ?? 0,
  }));
  return {
    options,
    notSet: { label: notSetLabel(column), count: notSetCount },
  };
};

export const isValueTicked = (column, filter, value) =>
  Boolean(
    filter?.values.some(
      (ticked) => toMatchKey(column, ticked) === toMatchKey(column, value)
    )
  );

export const filterValueLabels = (column, values = [], people = {}) => {
  const tickedByKey = new Map(
    values.map((value) => [toMatchKey(column, value), value])
  );
  return valueOptions(column, tickedByKey, people)
    .filter(({ value }) => tickedByKey.has(toMatchKey(column, value)))
    .map(({ label }) => label);
};
